import { AxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { useEditUser, useUser } from '../../../services/user/user.service';
import { genericErrorShowToastOptions, useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import UserForm from '../UserForm/UserForm';
import { UserFormErrors, UserFormValues } from '../UserForm/UserForm.types';


const EditUser: FC = () => {
  const navigateWithToast = useNavigateWithToast();
  const queryClient = useQueryClient();
  const { userId } = useParams();
  const loginId = userId as string; 
  const { data, isError: userIsError, error: userError } = useUser({loginId: loginId}, true);
  const userData = data?.data;
  const id = userData?.id

  const { mutate, isLoading } = useEditUser(id);

  useGenericErrorMessage([
      {isError: userIsError, error: userError}
  ]);

  const [formValues, setFormValues] = useState<UserFormValues>({
    fullName: '', 
    loginId: '', 
    webAccess: undefined,
    security: undefined,
    type: ''
  });

  useEffect(() => {
    if (userData) {
      const userValues = {
        fullName: userData.description,
        loginId: userData.loginId,
        security: userData.security,
        webAccess: userData.webAccess,
        type: userData.type
      };
      setFormValues(userValues);
      setInitialFormValues(userValues);
    }
  }, [userData]);

  const [initialFormValues, setInitialFormValues] = useState<UserFormValues>({
    fullName: '', 
    loginId: '', 
    webAccess: undefined, 
    security: undefined, 
  });

  const [formErrors, setFormErrors] = useState<UserFormErrors>({
    fullName: '', 
    loginId: '', 
    webAccess: '',
    security: '', 
  });

  const validateFormValues = () => {
    let error = false;
    let errorMessages: UserFormErrors = {
        fullName: '', 
        loginId: '', 
        webAccess: '',
        security: '', 
    };
    if ([null, undefined, ''].includes(formValues.fullName)  || 
        formValues.fullName.length < 1 || 
        formValues.fullName.length > 50) { 
            errorMessages.fullName = 'Full Name should be between 1 and 50 characters';
            error = true;
    } 
    if ([null, undefined, ''].includes(formValues.loginId)  || 
        formValues.loginId.length < 6 || 
        formValues.loginId.length > 50) { 
            errorMessages.loginId = 'LAN ID should be between 6 and 50 characters';
            error = true;
    } 
    if ([undefined, -1].includes(formValues.webAccess)) {
        errorMessages.webAccess = 'Web Access is required';
        error = true;
    }
    if ([undefined, -1].includes(formValues.security)) {
        errorMessages.security = 'Security is required';
        error = true;
    }
    setFormErrors({...formErrors, ...errorMessages})
    return error;
  };

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const checkForUnsavedChanges = () => {
    if (
      initialFormValues.fullName === formValues.fullName &&
      initialFormValues.loginId === formValues.loginId &&
      initialFormValues.webAccess === formValues.webAccess &&
      initialFormValues.security === formValues.security
    ) {
      setUnsavedChanges(false);
      return;
    } else {
      setUnsavedChanges(true);
      return;
    }
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues]);

  const handleSave = (event: { preventDefault: () => void; }) => {
    event.preventDefault();
    const isError = validateFormValues();
    if (isError) return;
    setUnsavedChanges(false);
    mutate(
      {
        description: formValues.fullName,
        loginId: formValues.loginId,
        security: formValues.security as number,
        webAccess : formValues.webAccess as number,
        type: formValues.type,
        deleted: userData?.deleted,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['user', id, loginId]);
          navigateWithToast(`/tables/users`, {
            label: 'User has been updated successfully',
            id: 'userUpdateSuccess',
          });
        },
        onError: (e) => { 
          if ((e as AxiosError)?.message === 'Request failed with status code 409') {
              showToast({
                  label: 'User with the provided LAN ID already exists.', 
                  id: 'editUserDuplicateUserError', 
                  toastType: ToastTypes.Error,
              })
              setUnsavedChanges(true);
          } else { 
              showToast(genericErrorShowToastOptions);
          }
      }
      }
    );
  };

  return (
    <>
      <UserForm
        formValues={formValues}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        setFormValues={setFormValues}
        headerTitle={`View User ${userData?.loginId ? `- ${userData?.loginId}` : ''}`}
        saveDisabled={!unsavedChanges}
        handleSave={handleSave}
        isButtonLoading={isLoading}
      />
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default EditUser;
